<script setup lang="ts">
import { onMounted, inject, reactive, ref, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { capitalize } from 'lodash';
import { required, requiredIf } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { getTextVariant } from '@/utils/stringUtils';
import { resetTimer } from '@/utils/dateUtils';
import { useComponentComposable } from '@/components/useComponentComposable';
import { useCityStore } from '@/store/CityStore';
import { useTourManagementStore } from '@/store/TourManagementStore';
import { useToast } from 'primevue/usetoast';
import { useDialog } from 'primevue/usedialog';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import DatePicker from 'primevue/datepicker';
import Dialog from 'primevue/dialog';
import Fluid from 'primevue/fluid';
import Select from 'primevue/select';
import AutomatedTourList from './tour/AutomatedTourList.vue';
import ConfirmTourModal from './ConfirmTourModal.vue';
import LoadingSpinner from '../LoadingSpinner.vue';
import RetrievingModal from './RetrievingModal.vue';

const dialogRef = inject('dialogRef') as Ref;
const onClose = dialogRef?.value?.options?.emits?.onClose;

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();
const dialog = useDialog();
const toast = useToast();
const { formatCountryFromCity } = useComponentComposable();
const cityStore = useCityStore();
const tourStore = useTourManagementStore();
const byMonth = ref(true);
const byDate = ref(false);
const isLoading = ref(false);
const isProcessing = ref(false);
const isHasError = ref(false);
const isOpenTourList = ref(false);
const totalItem = ref();
const offset = ref(1);
const limit = 40;
const searchData: any = ref([]);
const listMonth = ref([{ month: '', value: '' }]);
const getCityGateways = ref([]);
const filterCityGateways = ref([]);
const getCityDestinations = ref([]);
const filterCityDestinations = ref([]);
const today = new Date();
const tomorrow = new Date().setDate(today.getDate() + 1);

const tours: any = reactive({
  departureCity: '',
  destinationCity: '',
  month: '',
  departureDate: null,
  duration: -1,
  codeTours: []
});

const rules = ref<any>({
  departureCity: { required },
  destinationCity: { required },
  duration: { required },
  departureDate: { requiredIfRef: requiredIf(byDate.value) },
  month: { requiredIfRef: requiredIf(byMonth.value) }
});

const v$: any = useVuelidate(rules.value, tours);

const listDuration = ref([
  { range: t('management.tour.duration.all'), value: -1 },
  { range: t('management.tour.duration.5_to_10'), value: 7 },
  { range: t('management.tour.duration.11_to_16'), value: 14 },
  { range: t('management.tour.duration.more'), value: 21 }
]);

const getFilterCityGateways = async () => {
  isLoading.value = true;
  cityStore
    .getCityGateways()
    .then((res: any) => {
      if (res?.length) {
        getCityGateways.value = res;
      }
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const getFilterCityDestinations = async () => {
  isLoading.value = true;
  cityStore
    .getCityDestinations()
    .then((res: any) => {
      if (res?.length) {
        getCityDestinations.value = formatCountryFromCity(res);
      }
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const searchGateway = (event: any) => {
  const query = event.query;
  if (!query.trim().length) {
    filterCityGateways.value = [...getCityGateways.value.slice(0, 100)];
  } else {
    filterCityGateways.value = getCityGateways.value.filter((gateways) => {
      return (
        gateways.gateCode?.toLowerCase().startsWith(query.toLowerCase()) ||
        gateways[`cityName${capitalize(currentLanguage)}`]?.toLowerCase().startsWith(query.toLowerCase()) ||
        gateways[`region${capitalize(currentLanguage)}`]?.toLowerCase().startsWith(query.toLowerCase()) ||
        gateways[`gateCountry${capitalize(currentLanguage)}`]?.toLowerCase().startsWith(query.toLowerCase()) ||
        gateways[`gateName${capitalize(currentLanguage)}`]?.toLowerCase().startsWith(query.toLowerCase()) ||
        gateways.referenceType?.toLowerCase().startsWith(query.toLowerCase())
      );
    });
  }
};

const selectGateway = (event: any) => {
  const gateway = event.value;
  if (Object.keys(gateway).length) {
    for (const gateKey in gateway) {
      if (gateKey !== 'parentTourId' && gateKey !== 'parentDestId') {
        tours[gateKey] = gateway[gateKey];
      }
      tours.gateReferenceType = gateway.referenceType;
    }
  }
};

const searchDestination = (event: any) => {
  const query = event.query;
  if (!query.trim().length) {
    filterCityDestinations.value = [...getCityDestinations.value.slice(0, 100)];
  } else {
    filterCityDestinations.value = getCityDestinations.value.filter((destination) => {
      return (
        destination.destCode?.toLowerCase().startsWith(query.toLowerCase()) ||
        destination[`cityName${capitalize(currentLanguage)}`]?.toLowerCase().startsWith(query.toLowerCase()) ||
        destination[`region${capitalize(currentLanguage)}`]?.toLowerCase().startsWith(query.toLowerCase()) ||
        destination[`destCountry${capitalize(currentLanguage)}`]?.toLowerCase().startsWith(query.toLowerCase()) ||
        destination[`destName${capitalize(currentLanguage)}`]?.toLowerCase().startsWith(query.toLowerCase()) ||
        destination.referenceType?.toLowerCase().startsWith(query.toLowerCase())
      );
    });
  }
};

const selectDestination = (event: any) => {
  const destination = event.value;
  if (destination && Object.keys(destination).length) {
    for (const destKey in destination) {
      if (destKey !== 'parentTourId') {
        tours[destKey] = destination[destKey];
      }
      tours.destReferenceType = destination.referenceType;
    }
  }
};

const getDetailAddress = (dataProps: any, type: string) => {
  return dataProps[`cityName${capitalize(currentLanguage)}`] + ', ' + dataProps[`region${capitalize(currentLanguage)}`] + ', ' + dataProps[`${type}Country${capitalize(currentLanguage)}`] + ', ' + dataProps.referenceType;
};

const getListMonthInRange11month = () => {
  const months = [
    t('common.month.jan'),
    t('common.month.feb'),
    t('common.month.mar'),
    t('common.month.apr'),
    t('common.month.may'),
    t('common.month.jun'),
    t('common.month.jul'),
    t('common.month.aug'),
    t('common.month.sep'),
    t('common.month.oct'),
    t('common.month.nov'),
    t('common.month.dec')
  ];
  const date = new Date();
  const currentMonth = new Date().getMonth();
  const next11Months: string[] = [];

  for (let i = 0; i < 12; i++) {
    const monthIndex = (currentMonth + i) % 12;
    const year = date.getFullYear() + Math.floor((currentMonth + i) / 12);
    next11Months.push(`${months[monthIndex]} ${year}`);
  }

  listMonth.value = next11Months.map((monthYear, index) => {
    const year = monthYear.split(' ').pop();
    const monthIndex = (currentMonth + index) % 12;
    const monthValue = (monthIndex + 1).toString().padStart(2, '0');
    return {
      month: monthYear,
      value: `${year}-${monthValue}-01T00:00:00`
    };
  });

  return listMonth.value;
};

getListMonthInRange11month();

const validateCheckbox = (type: string) => {
  if (type === 'month') {
    byDate.value = false;
    byMonth.value = true;
    tours.departureDate = null;
  } else {
    byDate.value = true;
    byMonth.value = false;
    tours.month = '';
  }
  rules.value.departureDate = { requiredIfRef: requiredIf(byDate.value) };
  rules.value.month = { requiredIfRef: requiredIf(byMonth.value) };
};

const openPreviewAutoSearch = (data: any, tourCode: string[]) => {
  const dialogRef = dialog.open(AutomatedTourList, {
    props: {
      header: t('management.tour.add_manual'),
      modal: true,
      style: {
        width: '85vw'
      }
    },
    data: {
      automatedTourData: data,
      tourCode: tourCode
    },
    emits: {
      onClose: () => {
        onClose();
        dialogRef.close();
      }
    }
  });
};

const openModalSearchError = () => {
  const dialogRef = dialog.open(ConfirmTourModal, {
    data: {
      message: t('management.tour.confirm.notice'),
      subMessage: t('management.tour.confirm.update_request'),
      isAlignCenter: true
    },
    props: {
      header: t('management.tour.confirm.title'),
      modal: true
    },
    emits: {
      isClose: () => {
        dialogRef.close();
      }
    }
  });
};

const stopLoadingModal = () => {
  isProcessing.value = false;
  isLoading.value = false;
};

const callSearchTourAuto = async (payload: any) => {
  const result = tourStore
    .searchTourAuto(payload)
    .then((res) => {
      isOpenTourList.value = true;
      totalItem.value = res.data.totalElements;
      searchData.value = [...res.data.tours];
    })
    .catch(() => {
      isOpenTourList.value = false;
      isHasError.value = true;
      stopLoadingModal();
      openModalSearchError();
    });

  return result;
};

const handleSubmit = async (tours: any) => {
  isLoading.value = true;
  isProcessing.value = true;
  isHasError.value = false;
  offset.value = 1;
  const payload = {
    departureCity: tours.departureCity.gateCode,
    destinationCity: tours.destinationCity.destId,
    month: tours.month,
    departureDate: tours.departureDate !== null ? resetTimer(tours.departureDate) : '',
    duration: tours.duration,
    codeTours: [],
    offset: offset.value
  };

  await callSearchTourAuto(payload);
  if (totalItem.value !== null) {
    const timesCall = Math.ceil(totalItem.value / limit);
    if (timesCall === 1 && isOpenTourList.value) {
      openPreviewAutoSearch(searchData.value, tours.codeTours);
      stopLoadingModal();
    }
    for (let i = 1; i < timesCall; i++) {
      offset.value += limit;
      const payloadRecall = {
        ...payload,
        offset: offset.value
      };

      if (!isHasError.value) {
        await tourStore
          .searchTourAuto(payloadRecall)
          .then((res) => {
            isProcessing.value = false;
            totalItem.value = res.data.totalElements;
            searchData.value = [...searchData.value, ...res.data.tours];
            if (timesCall - 1 == i && isOpenTourList.value) {
              openPreviewAutoSearch(searchData.value, tours.codeTours);
              stopLoadingModal();
            }
          })
          .catch(() => {
            isHasError.value = true;
            stopLoadingModal();
            openModalSearchError();
          })
          .finally(() => {
            if (!isHasError.value && timesCall - 1 === i) {
              stopLoadingModal();
            }
          });
      } else {
        stopLoadingModal();
      }
    }
  } else {
    stopLoadingModal();
  }
};

const validateDepartureTime = () => {
  if (byMonth.value && tours.month !== '') {
    handleSubmit(tours);
  } else if (byDate.value && tours.departureDate !== null) {
    handleSubmit(tours);
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('notification.fill_required') });
  }
};

const handleSearch = () => {
  v$.value.$validate();
  if (!v$.value.$error) {
    validateDepartureTime();
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('notification.fill_required') });
  }
};

onMounted(() => {
  getFilterCityGateways();
  getFilterCityDestinations();
});
</script>
<template>
  <div class="search-engine-modal">
    <LoadingSpinner v-if="isLoading" />
    <div>
      <Fluid>
        <div class="flex grid">
          <div class="col-6 gateway-list">
            <label for="dept-city" class="text-lg">{{ $t('worksheet.departure_city') }} <span class="text-primary">*</span></label>
            <AutoComplete
              v-model="tours.departureCity"
              :suggestions="filterCityGateways"
              @complete="searchGateway($event)"
              :placeholder="t('common.gateway')"
              :optionLabel="getTextVariant(currentLanguage, 'gateNameEn', 'gateNameFr')"
              completeOnFocus
              @change="selectGateway($event)"
              class="w-full"
              forceSelection
              id="dept-city"
              :class="v$.departureCity.$error ? 'p-invalid' : ''"
            >
              <template #option="slotProps">
                <div class="flex align-options-center">
                  <div>
                    <div>{{ slotProps.option[`gateName${capitalize(currentLanguage)}`] }} ({{ slotProps.option.gateCode }})</div>
                    <div class="text-xs">{{ getDetailAddress(slotProps.option, 'gate') }}</div>
                  </div>
                </div>
              </template>
            </AutoComplete>

            <p class="text-red-500 pt-1" v-if="v$.departureCity.$error">{{ $t('validate.required') }}</p>
          </div>
          <div class="col-6 destination-list">
            <label for="dest-city" class="text-lg capitalize">{{ $t('management.tour.dest_city') }} <span class="text-primary">*</span></label>
            <AutoComplete
              v-model="tours.destinationCity"
              :suggestions="filterCityDestinations"
              @complete="searchDestination($event)"
              :optionLabel="getTextVariant(currentLanguage, 'destNameEn', 'destNameFr')"
              completeOnFocus
              class="w-full"
              forceSelection
              :placeholder="t('common.destination')"
              @change="selectDestination($event)"
              id="dest-city"
              :class="v$.destinationCity.$error ? 'p-invalid' : ''"
            >
              <template #option="slotProps">
                <div v-if="slotProps.option.destCountryParent" class="font-bold">
                  {{ slotProps.option.destCountryParent }}
                </div>
                <div v-else>
                  <div>{{ slotProps.option[`destName${capitalize(currentLanguage)}`] }} ({{ slotProps.option.destCode }})</div>
                  <div class="text-xs">{{ getDetailAddress(slotProps.option, 'dest') }}</div>
                </div>
              </template>
            </AutoComplete>
            <p class="text-red-500 pt-1" v-if="v$.destinationCity.$error">{{ $t('validate.required') }}</p>
          </div>
        </div>
      </Fluid>

      <Fluid>
        <div class="flex grid">
          <div class="col-3">
            <div class="flex flex-column">
              <div class="flex mb-2">
                <Checkbox inputId="month" v-model="byMonth" :binary="true" @change="validateCheckbox('month')" />
                <label for="month" class="text-lg ml-2">{{ $t('management.tour.by_deptMonth') }}</label>
              </div>
              <Select id="month" :disabled="!byMonth" v-model="tours.month" :options="listMonth" :placeholder="$t('common.month_format')" optionLabel="month" optionValue="value" :class="v$.month.$error ? 'p-invalid' : ''"> </Select>
              <p class="text-red-500 pt-1" v-if="v$.month.$error">{{ $t('validate.required') }}</p>
            </div>
          </div>
          <div class="col-3">
            <div class="flex flex-column">
              <div class="flex mb-2">
                <Checkbox inputId="date" v-model="byDate" :binary="true" @change="validateCheckbox('date')" />
                <label for="date" class="text-lg ml-2">{{ $t('management.tour.by_deptDate') }}</label>
              </div>
              <DatePicker :disabled="!byDate" id="date" dateFormat="dd-M-yy" v-model="tours.departureDate" autocomplete="off" :placeholder="$t('common.date_format')" :minDate="new Date(tomorrow)" :class="v$.departureDate.$error ? 'p-invalid' : ''">
                <template #footer>
                  <p class="text-center">
                    <small>{{ $t('common.press_space') }}</small>
                  </p>
                </template>
              </DatePicker>
              <p class="text-red-500 pt-1" v-if="v$.departureDate.$error">{{ $t('validate.required') }}</p>
            </div>
          </div>

          <div class="col-6 mt-2">
            <label for="duration" class="text-lg">{{ $t('common.duration') }} <span class="text-primary">*</span></label>
            <Select id="duration" v-model="tours.duration" :options="listDuration" optionLabel="range" optionValue="value" :class="v$.duration.$error ? 'p-invalid w-full' : 'w-full'"> </Select>
            <p class="text-red-500 pt-1" v-if="v$.duration.$error">{{ $t('validate.required') }}</p>
          </div>
        </div>
      </Fluid>

      <Fluid>
        <div class="flex grid">
          <div class="col-6">
            <label for="search-tour-code" class="text-lg">{{ $t('management.tour.search_by_tour_code') }}</label>
            <AutoComplete v-model="tours.codeTours" inputId="inputChip" multiple fluid :typeahead="false" />
            <span class="mt-2 font-bold text-sm">*{{ $t('common.guide.enter_code') }}</span>
          </div>
        </div>
      </Fluid>

      <div class="text-center mt-5">
        <Button :loading="isLoading" :label="$t('common.buttons.search')" @click="handleSearch()" data-testid="search-btn"></Button>
      </div>
      <Dialog v-model:visible="isProcessing" modal :closable="!isProcessing">
        <template #header>
          <div></div>
        </template>
        <RetrievingModal :title="$t('management.tour.confirm.retrieve')" />
      </Dialog>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.search-engine-modal {
  label {
    display: block;
    margin-bottom: 0.25rem;
  }

  :deep(.p-autocomplete) {
    display: block !important;
  }
}
</style>
